import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { LocateFixed, RotateCcw, ZoomIn, ZoomOut } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ESRI_GREY_ATTRIBUTION, ESRI_GREY_REFERENCE_TILES, ESRI_GREY_TILES } from '../map/tiles'

/**
 * The hero map: a real, live map (Leaflet over the shared basemap in {@link ../map/tiles} - the
 * same stack {@link ../map/ProviderMap} uses on the signed-in discovery page), not an illustration.
 *
 * <p>It opens on a neutral default view - nobody's location is read without them asking for it.
 * Pressing "Use my location" is what triggers the browser's own permission prompt, exactly as on
 * the signed-in map pages; nothing here fires it on page load.
 *
 * <p>The scattered dots per category are illustrative, not a fabricated listing: no name, photo or
 * exact distance is attached to any of them, just a category label on hover, and their positions
 * are deterministic (seeded from the filter name) so switching filters back and forth doesn't
 * reshuffle the map. Once this shows a visitor's real neighbourhood, a dot claiming to be a
 * specific real person on their actual street would be a much stronger (and false) claim than a
 * handful of unlabelled category markers is.
 */

/** Ghaziabad - the same default the signed-in map centres on before any real location is known. */
const DEFAULT_CENTRE = { lat: 28.6692, lng: 77.4538 }
const DEFAULT_ZOOM = 13
const LOCATED_ZOOM = 15
const RADIUS_KM = 2
// A single category shows a deeper cluster of its own; "All dots" trades depth for breadth - a
// few from every category, consolidated onto one view, so picking it visibly shows *more* dots
// than any one category alone rather than just relabelling the same seven.
const DOT_COUNT = 6
const DOTS_PER_CATEGORY_IN_ALL = 3

const CATEGORY_FILTERS = ['Academics', 'Music', 'Tech & AI', 'Fitness', 'Languages']
const FILTERS = ['All dots', ...CATEGORY_FILTERS]

/** What a hovered sample dot calls itself, per filter - category only, never a name. */
const CATEGORY_LABELS: Record<string, string[]> = {
  Academics: ['Maths tutor', 'Science tutor', 'English tutor', 'Commerce tutor'],
  Music: ['Guitar coach', 'Vocal coach', 'Keyboard coach', 'Drums coach'],
  'Tech & AI': ['Coding mentor', 'Web dev mentor', 'AI/ML mentor', 'Data mentor'],
  Fitness: ['Yoga coach', 'Badminton coach', 'Football coach', 'Fitness trainer'],
  Languages: ['Spoken English coach', 'French tutor', 'German tutor', 'Sanskrit tutor'],
}

/** Small deterministic PRNG so a given filter always scatters its dots the same way. */
function seededRandom(seed: number): () => number {
  let s = seed >>> 0
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0
    return s / 4294967296
  }
}

function hashSeed(value: string): number {
  let h = 0
  for (let i = 0; i < value.length; i++) h = (h * 31 + value.charCodeAt(i)) >>> 0
  return h
}

/** Scatters `count` points within ~1.6km of centre, deterministically per category. */
function categoryDots(category: string, centre: { lat: number; lng: number }, count: number) {
  const labels = CATEGORY_LABELS[category] ?? CATEGORY_LABELS.Academics
  const rand = seededRandom(hashSeed(category))
  const latPerKm = 1 / 111
  const lngPerKm = 1 / (111 * Math.cos((centre.lat * Math.PI) / 180))
  return Array.from({ length: count }, (_, i) => {
    const angle = rand() * Math.PI * 2
    const distanceKm = 0.25 + rand() * 1.35
    return {
      lat: centre.lat + Math.sin(angle) * distanceKm * latPerKm,
      lng: centre.lng + Math.cos(angle) * distanceKm * lngPerKm,
      label: labels[i % labels.length],
    }
  })
}

/**
 * "All dots" consolidates every category's own cluster onto one view (more markers, one from
 * each), rather than showing a generic set of the same size as a single category.
 */
function sampleDots(filter: string, centre: { lat: number; lng: number }) {
  if (filter === 'All dots') {
    return CATEGORY_FILTERS.flatMap((category) => categoryDots(category, centre, DOTS_PER_CATEGORY_IN_ALL))
  }
  return categoryDots(filter, centre, DOT_COUNT)
}

export function HeroDiscoveryMap() {
  const [active, setActive] = useState('All dots')
  const [locating, setLocating] = useState(false)
  const [located, setLocated] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [centre, setCentre] = useState(DEFAULT_CENTRE)

  // The badge reads however many sample dots the current filter actually draws, rather than a
  // fixed marketing number that would stay "28+" even for a single, six-dot category.
  const dotCount = sampleDots(active, centre).length

  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<L.Map | null>(null)
  const baseLayerRef = useRef<L.LayerGroup | null>(null)
  const dotsLayerRef = useRef<L.LayerGroup | null>(null)

  // Create once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return
    const map = L.map(containerRef.current, {
      scrollWheelZoom: false,
      zoomControl: false,
      attributionControl: false,
    }).setView([DEFAULT_CENTRE.lat, DEFAULT_CENTRE.lng], DEFAULT_ZOOM)
    const tileOptions = {
      // See ProviderMap: no tile data past z16 here, so upscale rather than show Esri's own
      // "Map data not yet available" placeholder.
      maxNativeZoom: 16,
      maxZoom: 19,
    }
    L.tileLayer(ESRI_GREY_TILES, { attribution: ESRI_GREY_ATTRIBUTION, ...tileOptions }).addTo(map)
    // The street linework and labels, stacked on the base fill above - see tiles.ts.
    L.tileLayer(ESRI_GREY_REFERENCE_TILES, tileOptions).addTo(map)
    baseLayerRef.current = L.layerGroup().addTo(map)
    dotsLayerRef.current = L.layerGroup().addTo(map)
    mapRef.current = map

    // The card's width depends on the brand webfonts, which can still be loading when this effect
    // runs - a map measured before they settle can end up a few pixels short and show grey slivers.
    const resize = () => map.invalidateSize()
    const t = setTimeout(resize, 300)
    window.addEventListener('resize', resize)
    return () => {
      clearTimeout(t)
      window.removeEventListener('resize', resize)
      map.remove()
      mapRef.current = null
      baseLayerRef.current = null
      dotsLayerRef.current = null
    }
  }, [])

  // Re-centre, and redraw the radius ring + "you are here" marker, whenever the centre changes.
  useEffect(() => {
    const map = mapRef.current
    const layer = baseLayerRef.current
    if (!map || !layer) return
    map.setView([centre.lat, centre.lng], located ? LOCATED_ZOOM : DEFAULT_ZOOM)
    layer.clearLayers()
    L.circle([centre.lat, centre.lng], {
      radius: RADIUS_KM * 1000,
      color: '#f59e0b',
      weight: 1.5,
      dashArray: '6 6',
      fillOpacity: 0.05,
    }).addTo(layer)
    if (located) {
      L.circleMarker([centre.lat, centre.lng], {
        radius: 8,
        color: '#0a192f',
        weight: 3,
        fillColor: '#ffffff',
        fillOpacity: 1,
      })
        .bindTooltip('You are here', { permanent: true, direction: 'top', offset: [0, -6] })
        .addTo(layer)
    }
  }, [centre, located])

  // Redraw the sample category dots whenever the filter or the centre changes.
  useEffect(() => {
    const layer = dotsLayerRef.current
    if (!layer) return
    layer.clearLayers()
    for (const dot of sampleDots(active, centre)) {
      L.circleMarker([dot.lat, dot.lng], {
        radius: 6,
        color: '#0a192f',
        weight: 2,
        fillColor: '#FFBE1A',
        fillOpacity: 1,
      })
        .bindTooltip(dot.label, { direction: 'top', offset: [0, -4] })
        .addTo(layer)
    }
  }, [active, centre])

  function useMyLocation() {
    if (!navigator.geolocation) {
      setError('Location isn’t available in this browser.')
      return
    }
    setLocating(true)
    setError(null)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocating(false)
        setLocated(true)
        setCentre({ lat: pos.coords.latitude, lng: pos.coords.longitude })
      },
      () => {
        setLocating(false)
        setError('Location access was blocked — showing a sample area instead.')
      },
      { enableHighAccuracy: true, timeout: 10000 },
    )
  }

  return (
    <div className="relative w-full max-w-none sm:max-w-[460px] lg:max-w-[560px]">
      {/* Below lg, badge/filters/caption/button are position:absolute children of THIS div, laid
          directly over the map tile - the actual Google Maps pattern (floating chips over a
          full-bleed map), not a card with the map as one section inside it. At lg+ they all
          switch to lg:static, which puts them back in normal DOM flow around the map tile exactly
          as before - same elements, same order, no duplicated markup, just repositioned. The
          bordered white card only appears at lg+ too, once there is room for it to read as a card
          instead of eating into the map itself. */}
      <div className="relative overflow-hidden rounded-none border-0 bg-transparent p-0 shadow-none lg:rounded-[28px] lg:border-[3px] lg:border-dot-400 lg:bg-white lg:p-3 lg:shadow-[0_24px_60px_-24px_rgba(15,23,42,0.35)]">
        {/* Count/radius badge + zoom controls. */}
        <div className="absolute inset-x-0 top-0 z-[1000] flex items-center justify-between gap-2 p-3 lg:static lg:p-0">
          <span className="inline-flex min-w-0 items-center gap-2 truncate rounded-full bg-deep-900 px-3 py-1.5 text-[11px] font-bold text-white shadow-lg lg:shadow-none">
            <span className="h-2 w-2 shrink-0 rounded-full bg-dot-400" />
            <span className="truncate">{dotCount} YellowDots <span className="text-white/45">·</span> 2km Local Radius</span>
          </span>
          <span className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              aria-label="Zoom in"
              onClick={() => mapRef.current?.zoomIn()}
              className="grid h-7 w-7 place-items-center rounded-full bg-white text-slate-500 shadow-lg hover:bg-slate-100 lg:h-6 lg:w-6 lg:border lg:border-slate-200 lg:shadow-none"
            >
              <ZoomIn size={12} className="lg:hidden" />
              <ZoomIn size={11} className="hidden lg:block" />
            </button>
            <button
              type="button"
              aria-label="Zoom out"
              onClick={() => mapRef.current?.zoomOut()}
              className="grid h-7 w-7 place-items-center rounded-full bg-white text-slate-500 shadow-lg hover:bg-slate-100 lg:h-6 lg:w-6 lg:border lg:border-slate-200 lg:shadow-none"
            >
              <ZoomOut size={12} className="lg:hidden" />
              <ZoomOut size={11} className="hidden lg:block" />
            </button>
            <button
              type="button"
              aria-label="Reset map view"
              onClick={() => mapRef.current?.setView([DEFAULT_CENTRE.lat, DEFAULT_CENTRE.lng], DEFAULT_ZOOM)}
              className="grid h-7 w-7 place-items-center rounded-full bg-white text-slate-500 shadow-lg hover:bg-slate-100 lg:h-6 lg:w-6 lg:border lg:border-slate-200 lg:shadow-none"
            >
              <RotateCcw size={12} className="lg:hidden" />
              <RotateCcw size={11} className="hidden lg:block" />
            </button>
          </span>
        </div>

        {/* Category filters - a floating, horizontally-scrollable pill row below lg (the Google
            Maps "Things to do / Museums / Transit" pattern), a wrapped row in normal flow at lg+.
            The sample dots redraw per category; nothing else here is wired to real data. */}
        <div className="absolute inset-x-0 top-14 z-[1000] overflow-x-auto px-3 [scrollbar-width:none] lg:static lg:mt-2.5 lg:overflow-visible lg:px-0 [&::-webkit-scrollbar]:hidden">
          <div className="flex gap-1.5 lg:flex-wrap">
            {FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setActive(f)}
                aria-pressed={active === f}
                className={`shrink-0 whitespace-nowrap rounded-full px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-wide shadow-lg transition lg:rounded-md lg:px-2 lg:py-1 lg:text-[9.5px] lg:shadow-none ${
                  active === f
                    ? 'bg-dot-400 text-deep-900'
                    : 'bg-white text-slate-500 hover:bg-slate-100'
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        </div>

        {/* The map tile itself. Full-bleed and tall below lg so it reads as the hero, not a small
            illustration under the real content; a conventional aspect-[4/3] card panel at lg+.
            isolate + z-0 pins Leaflet's internal stacking (its panes run to z-index 400-800) inside
            this box - see the identical note on ProviderMap - so the z-[1000] overlays above
            always win regardless of what Leaflet itself does internally. */}
        <div className="relative isolate z-0 h-[68vh] min-h-[460px] w-full overflow-hidden rounded-none bg-[#EDEAE3] lg:mt-2.5 lg:aspect-[4/3] lg:h-auto lg:min-h-0 lg:rounded-2xl">
          <div ref={containerRef} role="application" aria-label="Map centred near Ghaziabad" className="h-full w-full" />
        </div>

        {/* Caption + "Use my location" - a floating footer below lg, normal flow under the map at
            lg+. It is the one live control this map has for a signed-out visitor, and the same
            "Use my location" flow the signed-in map pages use. */}
        <div className="absolute inset-x-0 bottom-0 z-[1000] space-y-1.5 p-3 lg:static lg:mt-2.5 lg:space-y-1.5 lg:p-0">
          <p className="rounded-full bg-white/90 px-3 py-1 text-center text-[9.5px] font-semibold text-slate-500 shadow lg:bg-transparent lg:px-0 lg:py-0 lg:text-slate-400 lg:shadow-none">
            Sample dots shown for illustration &bull; real listings appear after you{' '}
            <Link to="/register" className="pointer-events-auto underline underline-offset-2 hover:text-slate-600">
              register
            </Link>
          </p>
          <button
            type="button"
            onClick={useMyLocation}
            disabled={locating}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-deep-900 px-4 py-2.5 text-[11px] font-extrabold uppercase tracking-wide text-white shadow-xl transition hover:bg-deep-700 disabled:cursor-not-allowed disabled:opacity-70 lg:rounded-2xl lg:shadow-[0_6px_20px_-10px_rgba(10,25,47,0.6)]"
          >
            <LocateFixed size={13} className={locating ? 'animate-pulse' : ''} />
            {locating ? 'Locating…' : located ? 'Showing your area' : 'Use my location'}
          </button>
          {error && (
            <p className="rounded-full bg-white/90 px-3 py-1 text-center text-[10px] font-semibold text-red-600 shadow lg:bg-transparent lg:px-0 lg:shadow-none">
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
